import type { DateRangePickerValue } from '@lokalise/louis';

import type { ObjectType, Option, ProjectFilterData, Relation } from '@app/components/AdvancedFilterDropdown/types';
import type { Contributor, TaskStyleGuide } from '@app/features/project';

type Year = `${number}${number}${number}${number}`;
type Month = `${number}${number}`;
type Day = `${number}${number}`;
type Hour = `${number}${number}`;
type Minute = `${number}${number}`;
type Second = `${number}${number}`;
type DateString = `${Year}-${Month}-${Day} ${Hour}:${Minute}:${Second}`;

export type TaskTypeOption = 'translation' | 'review' | 'lqa_by_ai' | 'automatic_translation';
export type TaskActorOption = 'ai' | 'mt_deepl' | 'mt_google';

export type TaskLanguage = {
    id: string;
    project_task_id: string;
    lang_id: string;
    keys: string;
    words: string;
    leverage: null;
    status: 'completed' | 'in_progress' | 'created' | 'queued';
    completed_at: null | DateString;
    completed_by: null | string;
    name: string;
    cc_iso: string;
    keys_total: `${number}`;
    keys_done: `${number}`;
    base_words_total: `${number}`;
    base_words_done: `${number}`;
    progress: number;
};

export type Task = {
    id: string;
    task_type: TaskTypeOption;
    due_date: null | DateString;
    title: string;
    description: string;
    status: 'completed' | 'in_progress' | 'created' | 'queued';
    created_by_fullname: string;
    project_name: string;
    master_project_id: string;
    master_branching_enabled: '0' | '1';
    branch_name: null | string;
    source_lang_name: string;
    source_lang_cc_iso: string;
    langs: readonly TaskLanguage[];
    team_id: number;
    style_guides: TaskStyleGuide[];
};

export type ScopeFilterParams = {
    object: ObjectType;
    option: Option;
    relation: Relation;
    value: string | number | (string | number)[];
};

export type GetActiveTasksRequest = { projectId?: string } | void;
export type GetActiveTasksResponse = readonly Task[];

export type GetActiveTaskByIdRequest = { projectId?: string; taskId?: string } | void;
export type GetActiveTaskByIdResponse = Task;

export type CloseLanguageRequest = { taskId: string; languageId: string };
export type CloseLanguageResponse = {
    success: boolean;
    finished: boolean;
    closed_task: null | {
        task_id: string | number;
    };
    lang: unknown; // Complex types, not used at the moment, can be added later as required
    child_langs: unknown; // Complex types, not used at the moment, can be added later as required
    child_task_unqueued: boolean;
    child_task_language_unqueued: boolean;
    warnings?: readonly string[];
};

interface GetBadgesProject {
    insert_id: number;
    name: string;
    task_count: number;
}

interface GetBadgesProjects {
    [projectId: string]: readonly GetBadgesProject[];
}

export type GetBadgesResponse = {
    widget: number;
    projects: GetBadgesProjects;
};

export type GetTemplateTasksResponse = {
    value: number;
    label: string;
}[];

export type GetTemplateTaskInfoRawResponse = {
    languageIds: number[];
    sourceLanguageId: number;
    tasks: {
        id: number;
        title: string;
        description: string;
        task_type: TaskTypeOption;
        tagsAfterComplete: string[];
        targetLanguages: {
            id: number;
            title: string;
            contributors: Contributor[];
        }[];
        options: {
            auto_close_items: boolean;
            auto_close_languages: boolean;
            auto_close_task: boolean;
            do_lock_translations: boolean;
            restrict_cts: boolean;
            tag_on_complete: boolean;
            save_ai_translation_to_tm: boolean;
            use_tm_100_percent_match: boolean;
            use_tm_fuzzy_match: boolean;
        };
        customTranslationStatuses?: {
            id: number;
            title: string;
            color: string;
        }[];
    }[];
};

export type GetTemplateTaskInfoResponse = {
    languageIds: readonly number[];
    sourceLanguageId: number;
    tasks: readonly {
        id: number;
        title: string;
        description: string;
        taskType: TaskTypeOption;
        tagsAfterComplete: readonly string[];
        targetLanguages: readonly {
            id: number;
            title: string;
            contributors: readonly Contributor[];
        }[];
        options: {
            autoCloseItems: boolean;
            autoCloseLanguages: boolean;
            autoCloseTask: boolean;
            lockTranslations: boolean;
            restrictCts: boolean;
            tagOnComplete: boolean;
            saveAiTranslationToTm: boolean;
            useTm100PercentMatch: boolean;
            useTmFuzzyMatch: boolean;
        };
        customTranslationStatuses?: readonly {
            id: number;
            title: string;
            color: string;
        }[];
    }[];
};

export type GetVendorsDataResponse = {
    id: number;
    title: string;
    logoUrl: string;
    members: {
        id: number;
        name: string;
        email: string;
    }[];
    cards: {
        label: string;
        value: string;
    }[];
}[];

export type GetProjectFilterDataResponse = {
    data: ProjectFilterData;
};

export type FilterParam = {
    object: string;
    option: string;
    value: string | Date | string[] | number | DateRangePickerValue;
    relation: string;
};

export type GetProjectAffectedKeyCountRequest = {
    projectId: string;
    branchName: string;
    filterParams: FilterParam[];
};

export type GetProjectAffectedKeyCountResponse = {
    count: number;
};

export type AddCustomProjectFilterRequest = {
    projectId: string;
    name: string;
    filterParams: FilterParam[];
};

export type AddCustomProjectFilterResponse =
    | {
          err: 0;
          data: {
              id: string;
              label: string;
              params?: ScopeFilterParams[];
          };
      }
    | {
          err: 1;
          message: string;
      };

export type DeleteCustomProjectFilterRequest = {
    projectId: string;
    filterId: string;
};

export type DeleteCustomProjectFilterResponse =
    | {
          err: 0;
      }
    | {
          err: 1;
          message: string;
      };

export type GetProjectWordCountResponse = {
    langs: {
        [id: number]: {
            key_count: number;
            key_count_other: number;
            lqai_preferred_lang: boolean;
            target_word_count?: number;
            word_count?: number;
        };
    };
};

export type GetProjectWordCountRequest = {
    projectId: string;
    branchName: string | null;
    sourceLanguage: number;
    targetLanguages: number[];
    isMassTask: boolean;
    parentTaskId: number | null;
    filterParams: FilterParam[];
    wordCountFor?: WordCountType[];
};

export enum WordCountType {
    source = 'source',
    target = 'target',
}

type UserContributor = {
    id: number;
    type: 'user';
    email: string;
    name: string;
    logoUrl: string;
};

type ContributorGroup = {
    id: string | number;
    type: 'group';
    name: string;
    logoUrl: string;
    memberIds: number[];
    users: UserContributor[];
};

export type GetTaskContributorsResponse = {
    [id: number]: (UserContributor | ContributorGroup)[];
};

export type GetTaskContributorsRequest = {
    projectId: string;
    sourceLanguage: number;
    targetLanguages: number[];
    branchName: string;
    onlyReviewers: boolean;
    isAi?: boolean;
    includeTaskManagers?: boolean;
    includeTaskCreator?: boolean;
};

export type TemplateTaskGeneralInfo = {
    title: string;
    description: string;
    due: string;
};

export type CreateInheritedTaskRequest = {
    templateId: number;
    inheritedTasks: Record<string, TemplateTaskGeneralInfo>;
    filterParams?: ScopeFilterParams[];
    isMassTask: boolean;
};

export type TargetLanguage = {
    id: number;
    contributors: {
        id: string | number;
        type: 'user' | 'group';
    }[];
};

export type VendorRateCard = {
    vendorId: number;
    rateCardId: number;
};

export type CreateTaskRequest = {
    // General
    projectId: string;
    branchName: string;
    title: string;
    description: string;
    dueDate?: string;
    type: string;
    parentTaskId?: number;
    // Options
    autoCloseItems: boolean;
    autoCloseLanguages: boolean;
    autoCloseTask: boolean;
    tagOnCompleteTags: Array<string>;
    lockTranslations: boolean;
    restrictCts: boolean;
    ctsIds?: Array<number>;
    useAsTemplateName?: string;
    saveAiTranslationToTm: boolean;
    useTm100PercentMatch: boolean;
    useTmFuzzyMatch: boolean;
    // Scope
    isMassTask: boolean;
    filterParams?: ScopeFilterParams[];
    sourceLanguage: number;
    targetLanguages: TargetLanguage[];
    vendorRateCards?: VendorRateCard[];
};
